import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LedgerEntryLine,
  LedgerPage,
  SortOrder,
} from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Badge } from '../common/Catalyst/badge';
import { Button } from '../common/Catalyst/button';
import { Input } from '../common/Catalyst/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../common/Catalyst/table';
import { Text } from '../common/Catalyst/text';
import { FieldPicker } from '../common/Custom/FieldPicker';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  entryTypeFilter,
  formatBillingDate,
  formatEpochDate,
  formatMoney,
  signedAmountParts,
} from '../utils/billingHelpers';
import { ledgerEntryType } from './enumLabels';

interface Props {
  hoaId: string;
  homeId: string;
  canReverse: boolean;
  onReverse: (entry: LedgerEntryLine) => void;
  /** Bumped by the parent after a write, to force a reload. */
  reloadToken: number;
}

const PER_PAGE = 50;

/**
 * The ledger itself.
 *
 * Two properties that look like implementation detail and are not:
 *
 * **`running_balance` is the account's balance after that entry,
 * computed over every prior entry — not a running sum of the rows on
 * screen.** So a filtered view still reports the real position, and the
 * page's `opening_balance` is everything before the first row *in date
 * order*, not before the first row *shown*. Re-summing the visible
 * column client-side would produce numbers no other screen agrees with.
 *
 * **A reversal is shown against what it reverses**, not as an
 * unexplained opposite row. `reverses_entry_id` is the link, and an
 * entry that has been reversed is struck through — the pair is the
 * story, and either alone reads as a mistake.
 */
const HomeLedgerTable: React.FC<Props> = ({
  hoaId,
  homeId,
  canReverse,
  onReverse,
  reloadToken,
}) => {
  const navigate = useNavigate();
  const [page, setPage] = useState<LedgerPage | null>(null);
  const [pageNo, setPageNo] = useState(1);
  const [order, setOrder] = useState<SortOrder>('desc');
  const [types, setTypes] = useState<string[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await billingService.getHomeLedger({
        homeId,
        page: pageNo,
        perPage: PER_PAGE,
        order,
        entryType: entryTypeFilter(types),
        from: from || undefined,
        to: to || undefined,
      });
      setPage(data);
    } catch (err) {
      console.error('Error loading ledger:', err);
      setError(billingErrorMessage(err, "Couldn't load the ledger."));
    } finally {
      setLoading(false);
    }
  }, [homeId, pageNo, order, types, from, to]);

  useEffect(() => {
    void load();
  }, [load, reloadToken]);

  const entries = useMemo(() => page?.entries ?? [], [page]);

  /** Entry ids that something on this page reverses. */
  const reversedIds = useMemo(() => {
    const ids = new Set<string>();
    entries.forEach((e) => {
      if (e.reverses_entry_id) ids.add(e.reverses_entry_id);
    });
    return ids;
  }, [entries]);

  const byId = useMemo(() => {
    const map = new Map<string, LedgerEntryLine>();
    entries.forEach((e) => map.set(e.id, e));
    return map;
  }, [entries]);

  const source = (e: LedgerEntryLine): React.ReactNode => {
    if (!e.source_type) return null;
    if (e.source_type === 'assessment_run' && e.source_id) {
      // The run's source_id is "{run_id}:{home_id}".
      const runId = e.source_id.split(':')[0];
      return (
        <Button
          plain
          className="!p-0 !text-xs"
          onClick={() => navigate(`/hoa/${hoaId}/billing/runs/${runId}`)}
        >
          Billing run
        </Button>
      );
    }
    if (e.source_type === 'violation' && e.source_id) {
      const violationId = e.source_id.split(':')[0];
      return (
        <Button
          plain
          className="!p-0 !text-xs"
          onClick={() => navigate(`/violations/${violationId}`)}
        >
          Violation
        </Button>
      );
    }
    return (
      <span className="text-xs text-zinc-500 dark:text-zinc-400">
        {e.source_type.replace(/_/g, ' ')}
      </span>
    );
  };

  const toggleType = (value: string): void => {
    setPageNo(1);
    setTypes((t) =>
      t.indexOf(value) === -1 ? [...t, value] : t.filter((x) => x !== value)
    );
  };

  const total = page?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div>
      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="Couldn't load the ledger"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline
          />
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
            From
          </label>
          <Input
            type="date"
            value={from}
            onChange={(e) => {
              setPageNo(1);
              setFrom(e.target.value);
            }}
            className="w-40"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
            To
          </label>
          <Input
            type="date"
            value={to}
            onChange={(e) => {
              setPageNo(1);
              setTo(e.target.value);
            }}
            className="w-40"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
            Order
          </label>
          <FieldPicker
            label="Order"
            value={order}
            onChange={(v) => {
              setPageNo(1);
              setOrder(v as SortOrder);
            }}
            className="w-44"
            options={[
              { value: 'desc', label: 'Newest first' },
              { value: 'asc', label: 'Oldest first' },
            ]}
          />
        </div>
        {(types.length > 0 || from || to) && (
          <Button
            plain
            onClick={() => {
              setTypes([]);
              setFrom('');
              setTo('');
              setPageNo(1);
            }}
          >
            Clear filters
          </Button>
        )}
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {ledgerEntryType.options().map((o) => {
          const on = types.indexOf(o.value) !== -1;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => toggleType(o.value)}
              className={
                on
                  ? 'rounded-full border border-sky-500 bg-sky-50 dark:bg-sky-950 px-3 py-1 text-xs text-sky-700 dark:text-sky-300'
                  : 'rounded-full border border-zinc-300 dark:border-zinc-700 px-3 py-1 text-xs text-zinc-600 dark:text-zinc-400'
              }
            >
              {o.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : entries.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 p-6">
          <Text>
            {types.length > 0 || from || to
              ? 'No entries match these filters. The account still has its full history — clear the filters to see it.'
              : 'Nothing has been posted to this home’s ledger yet.'}
          </Text>
        </div>
      ) : (
        <>
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <Text>
              Balance before this page:{' '}
              <strong>{formatMoney(page?.opening_balance)}</strong> · after:{' '}
              <strong>{formatMoney(page?.closing_balance)}</strong>
            </Text>
            <Text className="text-xs">
              These are the account&rsquo;s real figures, including entries a
              filter is hiding.
            </Text>
          </div>

          <Table dense>
            <TableHead>
              <TableRow>
                <TableHeader>Date</TableHeader>
                <TableHeader>Type</TableHeader>
                <TableHeader>Description</TableHeader>
                <TableHeader>Due</TableHeader>
                <TableHeader className="text-right">Amount</TableHeader>
                <TableHeader className="text-right">Balance</TableHeader>
                {canReverse && <TableHeader> </TableHeader>}
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((e) => {
                const parts = signedAmountParts(e.amount);
                const isReversal = !!e.reverses_entry_id;
                const wasReversed = reversedIds.has(e.id);
                const reversedEntry = e.reverses_entry_id
                  ? byId.get(e.reverses_entry_id)
                  : undefined;
                return (
                  <TableRow key={e.id}>
                    <TableCell>
                      {formatBillingDate(e.effective_date)}
                      <div className="text-xs text-zinc-500 dark:text-zinc-400">
                        posted {formatEpochDate(e.posted_at)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge color={ledgerEntryType.color(e.entry_type)}>
                        {ledgerEntryType.label(e.entry_type)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className={wasReversed ? 'line-through' : ''}>
                        {e.description}
                      </span>
                      {isReversal && (
                        <div className="text-xs text-purple-700 dark:text-purple-400">
                          {reversedEntry
                            ? `Reverses: ${reversedEntry.description}`
                            : 'Reverses an earlier entry'}
                        </div>
                      )}
                      {wasReversed && (
                        <div className="text-xs text-zinc-500 dark:text-zinc-400">
                          Reversed — the pair nets to nothing.
                        </div>
                      )}
                      <div className="mt-0.5">{source(e)}</div>
                    </TableCell>
                    <TableCell>
                      {e.due_date ? formatBillingDate(e.due_date) : '—'}
                    </TableCell>
                    <TableCell
                      className={
                        parts.isCredit
                          ? 'text-right text-lime-700 dark:text-lime-400'
                          : 'text-right'
                      }
                    >
                      {parts.text}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatMoney(e.running_balance)}
                    </TableCell>
                    {canReverse && (
                      <TableCell>
                        {!isReversal && !wasReversed && (
                          <Button plain onClick={() => onReverse(e)}>
                            Reverse
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <div className="mt-4 flex items-center justify-between">
            <Text>
              {total} {total === 1 ? 'entry' : 'entries'} · page {pageNo} of{' '}
              {lastPage}
            </Text>
            <div className="flex gap-2">
              <Button
                plain
                disabled={pageNo <= 1}
                onClick={() => setPageNo((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                plain
                disabled={pageNo >= lastPage}
                onClick={() => setPageNo((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HomeLedgerTable;
